import { Link, NavLink, useNavigate } from "react-router-dom";
import { Heart, Menu, X, LogOut } from "lucide-react";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const links = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/courses", label: "Courses" },
  { to: "/free-resources", label: "Free Resources" },
  { to: "/toolbox", label: "Toolbox" },
  { to: "/coaching", label: "Coaching" },
  { to: "/ai-tools", label: "AI Tools" },
  { to: "/ask-coach", label: "Ask Coach" },
  { to: "/calculators", label: "Calculators" },
  { to: "/community", label: "Community" },
  { to: "/blog", label: "Blog" },
];

export default function Navbar({ darkOverlay = false }) {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const nav = useNavigate();

  const linkBase = darkOverlay
    ? "text-white/85 hover:text-white"
    : "text-neutral-700 hover:text-neutral-900";

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-40 backdrop-blur-xl ${darkOverlay ? "bg-black/40 border-b border-white/10" : "bg-white/70 border-b border-neutral-200"}`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 group">
          <span className={`inline-flex items-center justify-center w-9 h-9 rounded-full ${darkOverlay ? "bg-white/10" : "bg-brand-blush"}`}>
            <Heart className="w-4 h-4 text-brand-pink" fill="#E91E63" />
          </span>
          <div className="leading-tight">
            <div className={`font-display text-xl ${darkOverlay ? "text-white" : "text-neutral-900"}`}>
              Host <span className="font-script text-brand-pink">Hustle</span>
            </div>
            <div className={`text-[10px] tracking-[0.22em] uppercase ${darkOverlay ? "text-white/60" : "text-neutral-500"}`}>
              Smarter Hosting
            </div>
          </div>
        </Link>

        <nav className="hidden xl:flex items-center gap-6">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `text-sm font-medium transition-colors ${linkBase} ${isActive ? "!text-brand-pink" : ""}`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden xl:flex items-center gap-3">
          {user ? (
            <>
              <Button asChild variant="ghost" data-testid="nav-dashboard-btn" className="rounded-full">
                <Link to="/dashboard">Dashboard</Link>
              </Button>
              <Button
                onClick={() => { logout(); nav("/"); }}
                data-testid="nav-logout-btn"
                variant="outline"
                className="rounded-full border-neutral-300"
              >
                <LogOut className="w-4 h-4 mr-2" /> Sign out
              </Button>
            </>
          ) : (
            <>
              <Button asChild variant="ghost" data-testid="nav-login-btn" className={`rounded-full ${darkOverlay ? "text-white hover:bg-white/10" : ""}`}>
                <Link to="/login">Log in</Link>
              </Button>
              <Button asChild data-testid="nav-start-btn" className="rounded-full bg-brand-pink hover:bg-brand-pink/90 text-white btn-pink-glow">
                <Link to="/register">Start Learning</Link>
              </Button>
            </>
          )}
        </div>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen(!open)}
          className={`xl:hidden p-2 rounded-full ${darkOverlay ? "text-white" : "text-neutral-900"}`}
          aria-label="Toggle menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="xl:hidden bg-white border-t border-neutral-200 px-6 py-6 space-y-4">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              onClick={() => setOpen(false)}
              data-testid={`mobile-nav-${l.label.toLowerCase()}`}
              className={({ isActive }) => `block text-base ${isActive ? "text-brand-pink" : "text-neutral-800"}`}
            >
              {l.label}
            </NavLink>
          ))}
          <div className="pt-4 border-t border-neutral-200 flex gap-3">
            {user ? (
              <>
                <Button asChild className="flex-1 rounded-full bg-brand-pink text-white"><Link to="/dashboard" onClick={() => setOpen(false)}>Dashboard</Link></Button>
                <Button variant="outline" className="flex-1 rounded-full" onClick={() => { logout(); setOpen(false); nav("/"); }}>Sign out</Button>
              </>
            ) : (
              <>
                <Button asChild variant="outline" className="flex-1 rounded-full"><Link to="/login" onClick={() => setOpen(false)}>Log in</Link></Button>
                <Button asChild className="flex-1 rounded-full bg-brand-pink text-white"><Link to="/register" onClick={() => setOpen(false)}>Start</Link></Button>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
